/**
 * Huawei VDSL error-counter helpers for the port detail page.
 *
 * ``display statistics performance <p> line-showtime cpe ever-before``
 * parses into a flat ``{label: value}`` dict with vendor-native labels
 * ("Count of errored seconds", "INMAINPEQ3", …). These helpers turn that
 * into readable, grouped table rows instead of a raw JSON dump.
 */

export type CounterGroupKey = "seconds" | "failures" | "events" | "inm";

export type CounterRow = {
  key: string;
  label: string;
  value: unknown;
};

export type CounterGroup = {
  key: CounterGroupKey;
  title: string;
  rows: CounterRow[];
};

const GROUP_TITLES: Record<CounterGroupKey, string> = {
  seconds: "Seconds counters",
  failures: "Failures",
  events: "Other events",
  inm: "Impulse noise (INM)",
};

const ABBREV: Record<string, string> = {
  "forward error correction seconds": "FEC seconds",
  "errored seconds": "Errored seconds (ES)",
  "severely errored seconds": "Severely errored seconds (SES)",
  "unavailable seconds": "Unavailable seconds (UAS)",
};

/** "Count of errored seconds" → "Errored seconds (ES)". */
export function counterLabel(key: string): string {
  const stripped = key.replace(/^count of (the )?/i, "").trim();
  const abbrev = ABBREV[stripped.toLowerCase()];
  if (abbrev) return abbrev;
  if (!stripped) return key;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export function counterGroupOf(key: string): CounterGroupKey {
  if (/^INMA/i.test(key) || /INMAME/i.test(key)) return "inm";
  const k = key.toLowerCase();
  if (k.includes("failures")) return "failures";
  if (k.includes("seconds")) return "seconds";
  return "events";
}

/** Natural order for INMAINPEQ1..17 / INMAIAT0..7 (not 1, 10, 11, 2 …). */
function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

export function groupCounters(counters: Record<string, unknown>): CounterGroup[] {
  const buckets: Record<CounterGroupKey, CounterRow[]> = {
    seconds: [],
    failures: [],
    events: [],
    inm: [],
  };
  for (const [key, value] of Object.entries(counters)) {
    const g = counterGroupOf(key);
    buckets[g].push({
      key,
      label: g === "inm" && /^INMA/i.test(key) ? key : counterLabel(key),
      value,
    });
  }
  buckets.inm.sort((a, b) => naturalCompare(a.key, b.key));
  const order: CounterGroupKey[] = ["seconds", "failures", "events", "inm"];
  return order
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({ key: k, title: GROUP_TITLES[k], rows: buckets[k] }));
}

export function formatCounterValue(v: unknown): string {
  if (typeof v === "number") return v.toLocaleString();
  if (v == null) return "—";
  return String(v);
}
